import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { paymentService, contractService, userService } from '../../../services/api';
import { useAuth } from '../../../context/EcoAppsAuthContext';
import PaymentForm from '../../../components/Forms/PaymentForm';
import { PageContainer, PageHeader } from '../../../components/Layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  StatusBadge,
  Button,
  Select,
  EmptyState,
  Spinner,
  ConfirmModal,
  Modal,
} from '../../../components/UI';
import {
  Wallet,
  Plus,
  Calendar,
  Trash2,
  Eye,
  Edit,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  User,
  RefreshCw,
} from 'lucide-react';

const PaymentsList = () => {
  const { user } = useAuth();
  const isLandlord = user?.role === 'landlord';

  const [payments, setPayments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [rentFilters, setRentFilters] = useState({
    year: ''
  });
  const [serviceFilters, setServiceFilters] = useState({
    year: '',
    month: '',
    type: '',
    status: ''
  });
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [mobileStatusPayment, setMobileStatusPayment] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    fetchContracts();
    fetchTenants();
  }, []);

  useEffect(() => {
    if (tenants.length === 0 && contracts.length > 0) {
      const dedup = deriveTenantsFromContracts(contracts);
      setTenants(dedup);
    }
  }, [contracts, tenants.length]);

  useEffect(() => {
    fetchPayments();
  }, [selectedTenant]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const syncViewport = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewport);
      return () => mediaQuery.removeEventListener('change', syncViewport);
    }

    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await contractService.getAll({ page: 1, limit: 1000 });
      setContracts(response?.data?.data?.contracts || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await userService.getTenants();
      const tenantList = normalizeTenantResponse(response);
      if (tenantList.length > 0) {
        setTenants(tenantList);
        return;
      }
      const fallback = deriveTenantsFromContracts(contracts);
      setTenants(fallback);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      const fallback = deriveTenantsFromContracts(contracts);
      setTenants(fallback);
    }
  };

  const normalizeTenantResponse = (response) => {
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data?.data)) return response.data.data.data;
    return [];
  };

  const getTenantLookupId = (tenant) => tenant?.identity_user_id || tenant?.identityUserId || tenant?.id;

  const deriveTenantsFromContracts = (contractsList = []) => {
    const fallback = contractsList
      .filter((c) => c?.tenant_id || c?.tenantId)
      .map((c) => {
        const tenant = c?.tenant || {};
        return {
          id: c?.tenant_id || c?.tenantId || getTenantLookupId(tenant),
          identity_user_id: tenant?.identity_user_id || tenant?.identityUserId || c?.tenant_id || c?.tenantId,
          first_name: tenant?.first_name || tenant?.firstName || '',
          last_name: tenant?.last_name || tenant?.lastName || '',
          email: tenant?.email || ''
        };
      })
      .filter((t) => t?.id);

    return Array.from(new Map(fallback.map((t) => [t.id, t])).values());
  };

  const fetchPayments = async () => {
    if (!selectedTenant) {
      setPayments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = { page: 1, limit: 5000, tenantId: selectedTenant };
      const response = await paymentService.getAll(params);
      setPayments(response?.data?.data?.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (data) => {
    try {
      setSubmitting(true);
      await paymentService.create(data);
      setShowForm(false);
      fetchPayments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al crear el pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePayment = async (data) => {
    try {
      setSubmitting(true);
      await paymentService.update(editingPayment.id, data);
      setEditingPayment(null);
      setShowForm(false);
      fetchPayments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al actualizar el pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    try {
      setDeletingId(paymentToDelete.id);
      await paymentService.delete(paymentToDelete.id);
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
      fetchPayments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al eliminar el pago');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (payment, newStatus) => {
    if (!payment?.id || !newStatus || payment.status === newStatus) return;

    try {
      setUpdatingStatusId(payment.id);
      await paymentService.update(payment.id, { status: newStatus });
      setPayments((prev) =>
        prev.map((item) => (item.id === payment.id ? { ...item, status: newStatus } : item))
      );
    } catch (error) {
      alert(error.response?.data?.message || 'Error al actualizar el estado del pago');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const uniqueTenants = useMemo(() => tenants, [tenants]);

  const normalizeId = (value) => String(value || '').trim().toLowerCase();

  const getTenantOptionLabel = (tenant) => {
    const fullName = `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
    return fullName || tenant.email || `Arrendatario ${tenant.id}`;
  };

  const tenantOptions = useMemo(
    () => uniqueTenants.map((tenant) => ({
      value: String(getTenantLookupId(tenant) || ''),
      label: getTenantOptionLabel(tenant)
    })),
    [uniqueTenants]
  );

  const filteredPayments = useMemo(() => {
    if (!selectedTenant) return [];
    const tenantKey = normalizeId(selectedTenant);
    return payments
      .filter((p) => {
        const contractTenantId = normalizeId(p?.contract?.tenant_id || p?.contract?.tenantId);
        const paymentUserId = normalizeId(p?.user_id || p?.userId);
        return contractTenantId === tenantKey || paymentUserId === tenantKey;
      });
  }, [payments, selectedTenant]);

  const sortPaymentRows = (rows) => {
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => direction * (toDateValue(a?.due_date) - toDateValue(b?.due_date)));
  };

  const rentYearOptions = useMemo(() => {
    const years = new Set();
    filteredPayments
      .filter((p) => p.type === 'rent')
      .forEach((p) => {
        if (!p?.due_date) return;
        years.add(new Date(p.due_date).getFullYear());
      });

    return [...years]
      .sort((a, b) => b - a)
      .map((year) => ({ value: String(year), label: String(year) }));
  }, [filteredPayments]);

  const sortOrderOptions = useMemo(
    () => ([
      { value: 'desc', label: 'Más recientes primero' },
      { value: 'asc', label: 'Más antiguos primero' }
    ]),
    []
  );

  const rentPayments = useMemo(() => {
    const rows = filteredPayments.filter((p) => {
      if (p.type !== 'rent') return false;
      if (!rentFilters.year) return true;
      if (!p?.due_date) return false;
      return String(new Date(p.due_date).getFullYear()) === rentFilters.year;
    });

    return sortPaymentRows(rows);
  }, [filteredPayments, rentFilters.year, sortOrder]);

  const servicePayments = useMemo(
    () => filteredPayments.filter((p) => p.type !== 'rent'),
    [filteredPayments]
  );

  const serviceYearOptions = useMemo(() => {
    const years = new Set();
    servicePayments.forEach((p) => {
      if (!p?.due_date) return;
      years.add(new Date(p.due_date).getFullYear());
    });
    return [...years]
      .sort((a, b) => b - a)
      .map((year) => ({ value: String(year), label: String(year) }));
  }, [servicePayments]);

  const serviceTypeOptions = useMemo(() => {
    const known = ['electricity', 'water', 'gas', 'maintenance', 'other', 'deposit'];
    const set = new Set(known);
    servicePayments.forEach((p) => {
      if (p?.type && p.type !== 'rent') set.add(p.type);
    });
    return [...set].map((type) => ({ value: type, label: getTypeLabel(type) }));
  }, [servicePayments]);

  const serviceStatusOptions = useMemo(
    () => ([
      { value: 'pending', label: 'Pendiente' },
      { value: 'paid', label: 'Pagado' },
      { value: 'approved', label: 'Aprobado' },
      { value: 'overdue', label: 'Vencido' },
      { value: 'partial', label: 'Parcial' },
      { value: 'cancelled', label: 'Cancelado' }
    ]),
    []
  );

  const monthOptions = useMemo(
    () => ([
      { value: '1', label: 'Enero' },
      { value: '2', label: 'Febrero' },
      { value: '3', label: 'Marzo' },
      { value: '4', label: 'Abril' },
      { value: '5', label: 'Mayo' },
      { value: '6', label: 'Junio' },
      { value: '7', label: 'Julio' },
      { value: '8', label: 'Agosto' },
      { value: '9', label: 'Septiembre' },
      { value: '10', label: 'Octubre' },
      { value: '11', label: 'Noviembre' },
      { value: '12', label: 'Diciembre' }
    ]),
    []
  );

  const filteredServicePayments = useMemo(() => {
    const rows = servicePayments.filter((p) => {
      const due = p?.due_date ? new Date(p.due_date) : null;
      const yearOk = !serviceFilters.year || (due && String(due.getFullYear()) === serviceFilters.year);
      const monthOk = !serviceFilters.month || (due && String(due.getMonth() + 1) === serviceFilters.month);
      const typeOk = !serviceFilters.type || p?.type === serviceFilters.type;
      const statusOk = !serviceFilters.status || p?.status === serviceFilters.status;
      return yearOk && monthOk && typeOk && statusOk;
    });

    return sortPaymentRows(rows);
  }, [servicePayments, serviceFilters, sortOrder]);

  const summaryStats = useMemo(() => {
    const total = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const pending = filteredPayments
      .filter((p) => p.status === 'pending')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const paid = filteredPayments
      .filter((p) => p.status === 'paid' || p.status === 'approved')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const overdue = filteredPayments
      .filter((p) => p.status === 'overdue' || (p.status === 'pending' && new Date(p.due_date) < new Date()))
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    return { total, pending, paid, overdue };
  }, [filteredPayments]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

  function toDateValue(value) {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  function getTypeLabel(type) {
    const labels = {
      rent: 'Arriendo',
      electricity: 'Electricidad',
      water: 'Agua',
      gas: 'Gas',
      deposit: 'Depósito',
      maintenance: 'Mantenimiento',
      other: 'Otro'
    };
    return labels[type] || type;
  }

  const getPropertyName = (payment) => payment.contract?.property?.name || '-';

  const getStatusLabel = (status) => {
    const option = serviceStatusOptions.find((item) => item.value === status);
    return option?.label || status || 'Sin estado';
  };

  const isApprovedPayment = (payment) => payment?.status === 'approved';

  const handleEditClick = (payment) => {
    if (!['pending', 'paid'].includes(payment.status)) {
      return;
    }
    setEditingPayment(payment);
    setShowForm(true);
  };

  const closePaymentForm = () => {
    setShowForm(false);
    setEditingPayment(null);
  };

  const renderPaymentsTable = (title, rows) => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No hay pagos en esta categoría</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Propiedad</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Monto</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha de vencimiento</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100 rounded-lg mr-3">
                          <FileText className="w-4 h-4 text-primary-600" />
                        </div>
                        <span className="font-medium text-gray-900">{getPropertyName(payment)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{getTypeLabel(payment.type)}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{formatCurrency(payment.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(payment.due_date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isLandlord ? (
                        isMobileViewport ? (
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1">
                              <StatusBadge status={payment.status} />
                            </div>
                            {!isApprovedPayment(payment) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 border border-gray-200 bg-white hover:bg-gray-50"
                                disabled={updatingStatusId === payment.id}
                                onClick={() => setMobileStatusPayment(payment)}
                                aria-label="Actualizar estado del pago"
                                title="Actualizar estado del pago"
                              >
                                <RefreshCw className={`w-4 h-4 ${updatingStatusId === payment.id ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div>
                            {isApprovedPayment(payment) ? (
                              <StatusBadge status={payment.status} size="sm" />
                            ) : (
                              <Select
                                value={payment.status || ''}
                                onChange={(e) => handleStatusChange(payment, e.target.value)}
                                options={serviceStatusOptions}
                                disabled={updatingStatusId === payment.id}
                              />
                            )}
                          </div>
                        )
                      ) : (
                        <StatusBadge status={payment.status} size="sm" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link to={`/rentalbiz/payments/${payment.id}`}>
                          <Button variant="ghost" size="sm" className="p-2">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {isLandlord && ['pending', 'paid'].includes(payment.status) && (
                          <Button variant="ghost" size="sm" className="p-2" onClick={() => handleEditClick(payment)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {isLandlord && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setPaymentToDelete(payment);
                              setShowDeleteConfirm(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Pagos"
          subtitle="Selecciona un arrendatario para ver los pagos por categoría"
          actions={
            isLandlord && (
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/rentalbiz/payments/scheduler">
                  <Button variant="secondary" className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Programar pagos
                  </Button>
                </Link>
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingPayment(null);
                    setShowForm(true);
                  }}
                  className="flex items-center"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Crear pago
                </Button>
              </div>
            )
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-blue-600 font-medium">Total</p><p className="text-xl font-bold text-blue-700">{formatCurrency(summaryStats.total)}</p></div><div className="p-2 bg-blue-200 rounded-lg"><DollarSign className="w-5 h-5 text-blue-700" /></div></div></CardContent></Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-yellow-600 font-medium">Pendientes</p><p className="text-xl font-bold text-yellow-700">{formatCurrency(summaryStats.pending)}</p></div><div className="p-2 bg-yellow-200 rounded-lg"><Clock className="w-5 h-5 text-yellow-700" /></div></div></CardContent></Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-green-600 font-medium">Pagados</p><p className="text-xl font-bold text-green-700">{formatCurrency(summaryStats.paid)}</p></div><div className="p-2 bg-green-200 rounded-lg"><CheckCircle className="w-5 h-5 text-green-700" /></div></div></CardContent></Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-red-600 font-medium">Vencidos</p><p className="text-xl font-bold text-red-700">{formatCurrency(summaryStats.overdue)}</p></div><div className="p-2 bg-red-200 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-700" /></div></div></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Arrendatarios</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              placeholder="Selecciona un arrendatario"
              options={tenantOptions}
            />
          </CardContent>
        </Card>

        {!selectedTenant ? (
          <Card>
            <CardContent className="p-8">
              <EmptyState icon={User} title="Selecciona un arrendatario" description="Los pagos apareceran cuando selecciones un arrendatario." />
            </CardContent>
          </Card>
        ) : loading ? (
          <Card>
            <CardContent className="p-8 flex items-center justify-center"><Spinner size="lg" /></CardContent>
          </Card>
        ) : filteredPayments.length === 0 ? (
          <Card>
            <CardContent className="p-8">
              <EmptyState icon={Wallet} title="No hay pagos" description="No se encontraron pagos para este arrendatario." />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Filtros de arriendo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    value={rentFilters.year}
                    onChange={(e) => setRentFilters((prev) => ({ ...prev, year: e.target.value }))}
                    placeholder="Año"
                    options={rentYearOptions}
                  />
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    placeholder="Orden"
                    options={sortOrderOptions}
                  />
                </div>
              </CardContent>
            </Card>
            {renderPaymentsTable('Pagos de arriendo', rentPayments)}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Filtros de servicios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Select
                    value={serviceFilters.year}
                    onChange={(e) => setServiceFilters((prev) => ({ ...prev, year: e.target.value }))}
                    placeholder="Año"
                    options={serviceYearOptions}
                  />
                  <Select
                    value={serviceFilters.month}
                    onChange={(e) => setServiceFilters((prev) => ({ ...prev, month: e.target.value }))}
                    placeholder="Mes"
                    options={monthOptions}
                  />
                  <Select
                    value={serviceFilters.type}
                    onChange={(e) => setServiceFilters((prev) => ({ ...prev, type: e.target.value }))}
                    placeholder="Tipo de servicio"
                    options={serviceTypeOptions}
                  />
                  <Select
                    value={serviceFilters.status}
                    onChange={(e) => setServiceFilters((prev) => ({ ...prev, status: e.target.value }))}
                    placeholder="Estado"
                    options={serviceStatusOptions}
                  />
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    placeholder="Orden"
                    options={sortOrderOptions}
                  />
                </div>
              </CardContent>
            </Card>
            {renderPaymentsTable('Servicios (Agua, Electricidad, Gas y otros)', filteredServicePayments)}
          </div>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={closePaymentForm}
        size="xl"
        title={editingPayment ? 'Editar pago' : 'Crear pago'}
        className="max-h-[90vh] overflow-hidden"
      >
        <div className="max-h-[calc(90vh-88px)] overflow-y-auto pr-1">
          <PaymentForm
            payment={editingPayment}
            contracts={contracts}
            onSubmit={editingPayment ? handleUpdatePayment : handleCreatePayment}
            onCancel={closePaymentForm}
            loading={submitting}
          />
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPaymentToDelete(null);
        }}
        onConfirm={handleDeletePayment}
        title="Eliminar Pago"
        message={`Estas seguro de eliminar este pago de ${paymentToDelete ? formatCurrency(paymentToDelete.amount) : ''}? Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deletingId !== null}
      />

      <Modal
        isOpen={!!mobileStatusPayment}
        onClose={() => setMobileStatusPayment(null)}
        size="sm"
        title="Cambiar estado del pago"
      >
        {mobileStatusPayment && (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Estado actual</p>
              <div className="mt-2">
                <StatusBadge status={mobileStatusPayment.status} />
              </div>
              <p className="mt-3 text-sm text-gray-600">
                {getPropertyName(mobileStatusPayment)} · {formatCurrency(mobileStatusPayment.amount)}
              </p>
            </div>

            <div className="space-y-2">
              {serviceStatusOptions.map((option) => {
                const isSelected = option.value === mobileStatusPayment.status;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    disabled={updatingStatusId === mobileStatusPayment.id || isSelected}
                    onClick={async () => {
                      await handleStatusChange(mobileStatusPayment, option.value);
                      setMobileStatusPayment(null);
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">{option.label}</span>
                      {isSelected && <span className="text-xs">Actual</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setMobileStatusPayment(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};

export default PaymentsList;
